import { chain, defaults, map, omit } from 'lodash';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { COMMON_PROPS } from '@/trendData/baseItem.constants';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import type { AnyProperty } from '@/utilities.types';
import { ITEM_DATA_STATUS, ITEM_TYPES } from '@/trendData/trendData.constants';
import {
  COMPARISON_TABLE_PROPS,
  SUMMARY_REPORT_TABLE_PROPS,
  X_AXIS_LABEL,
  Y_AXIS_LABEL,
} from '@/summaryReport/summaryData.constants';

export class SummaryReportStore extends BaseItemStore<StatisticsSummaryItem> {
  static readonly storeName = 'sqSummaryReportStore';

  initialize() {
    this.state = this.immutable(
      defaults(
        {
          items: [],
          baseline: null,
          isItemsOverlaid: false,
          showHistogram: true,
          showBoxPlot: true,
          showTable: true,
          showBellCurve: true,
          status: ITEM_DATA_STATUS.INITIALIZING,
          xAxisLabel: X_AXIS_LABEL.UNIT,
          yAxisLabel: Y_AXIS_LABEL.COUNT,
          showBoxplotPoints: true,
          visibleStats: [...SUMMARY_REPORT_TABLE_PROPS, ...COMPARISON_TABLE_PROPS].map((prop) => prop.key).sort(),
          showSignal: true,
          showTimeRange: true,
          showCondition: true,
        },
        COMMON_PROPS,
      ),
    );
  }

  get items() {
    return this.state.get('items');
  }

  get baseline() {
    return this.state.get('baseline');
  }

  get isItemsOverlaid() {
    return this.state.get('isItemsOverlaid');
  }

  get showHistogram() {
    return this.state.get('showHistogram');
  }

  get showBoxPlot() {
    return this.state.get('showBoxPlot');
  }

  get showTable() {
    return this.state.get('showTable');
  }

  get status() {
    return this.state.get('status');
  }

  get visibleStats() {
    return this.state.get('visibleStats');
  }

  get showBellCurve() {
    return this.state.get('showBellCurve');
  }

  get xAxisLabel() {
    return this.state.get('xAxisLabel');
  }

  get yAxisLabel() {
    return this.state.get('yAxisLabel');
  }

  get showSignal() {
    return this.state.get('showSignal');
  }

  get showTimeRange() {
    return this.state.get('showTimeRange');
  }

  get showCondition() {
    return this.state.get('showCondition');
  }

  get showBoxplotPoints() {
    return this.state.get('showBoxplotPoints');
  }

  dehydrate() {
    return {
      items: chain(this.state.get('items'))
        .map((item) => this.pruneDehydratedItemProps(item))
        .value(),
      isItemsOverlaid: this.isItemsOverlaid,
      showHistogram: this.showHistogram,
      showBoxPlot: this.showBoxPlot,
      showTable: this.showTable,
      visibleStats: this.visibleStats,
      showBellCurve: this.showBellCurve,
      xAxisLabel: this.xAxisLabel,
      yAxisLabel: this.yAxisLabel,
      showSignal: this.showSignal,
      showTimeRange: this.showTimeRange,
      showCondition: this.showCondition,
      baseline: this.baseline,
      showBoxplotPoints: this.showBoxplotPoints,
    };
  }

  async rehydrate(dehydratedState: AnyProperty) {
    this.state.merge(omit(dehydratedState, ['items']));
    this.state.set(
      'items',
      map(dehydratedState.items || [], (item) =>
        this.createItem(item.id, item.name, ITEM_TYPES.FORMULA_ITEM as any, omit(item, ['id', 'name', 'itemType'])),
      ),
    );
  }

  setItems(payload: { items: StatisticsSummaryItem[] }) {
    const items = payload.items?.map((item) => ({ ...item, itemType: ITEM_TYPES.FORMULA_ITEM }));
    this.state.set('items', items);
  }

  setBaseline(payload: { item: StatisticsSummaryItem | null }) {
    this.state.set('baseline', payload.item);
  }

  updateItem(payload: { item: StatisticsSummaryItem }) {
    if (this.baseline && payload.item.id === this.baseline.id) {
      this.setBaseline({ item: payload.item });
    }

    const updatedItems = map(this.items, (item) => (item.id === payload.item.id ? payload.item : item));
    this.setItems({ items: updatedItems });
  }

  addItem(payload: { item: StatisticsSummaryItem }) {
    const currentItems = this.items;
    if (!this.findItem(payload.item.id)) {
      this.setItems({ items: [...currentItems, payload.item] });
    } else {
      this.updateItem(payload);
    }
  }

  insertItemAtIndex(payload: { item: StatisticsSummaryItem; index: number }) {
    const { item, index } = payload;
    const items = this.items ?? [];
    const without = items.filter((x: any) => x.id !== item.id);
    const clamped = Math.max(0, Math.min(index, without.length));
    const next = [...without.slice(0, clamped), item, ...without.slice(clamped)];
    this.setItems({ items: next });
  }

  setStatus(payload: { status: ITEM_DATA_STATUS }) {
    this.state.set('status', payload.status);
  }

  setIsItemsOverlaid(payload: { isItemsOverlaid: boolean }) {
    this.state.set('isItemsOverlaid', payload.isItemsOverlaid);
  }

  removeItems(payload: { items: StatisticsSummaryItem[] }) {
    // Call the parent's removeItems method which handles color tracking
    super.removeItems(payload);
  }

  setShowHistogram(payload: { showHistogram: boolean }) {
    this.state.set('showHistogram', payload.showHistogram);
  }

  setShowBoxPlot(payload: { showBoxPlot: boolean }) {
    this.state.set('showBoxPlot', payload.showBoxPlot);
  }

  setShowTable(payload: { showTable: boolean }) {
    this.state.set('showTable', payload.showTable);
  }

  setVisibleStats(payload: { visibleStats: string[] }) {
    this.state.set('visibleStats', payload.visibleStats);
  }

  setShowBellCurve(payload: { showBellCurve: boolean }) {
    this.state.set('showBellCurve', payload.showBellCurve);
  }

  setXAxisLabel(payload: { xAxisLabel: X_AXIS_LABEL }) {
    this.state.set('xAxisLabel', payload.xAxisLabel);
  }

  setYAxisLabel(payload: { yAxisLabel: Y_AXIS_LABEL }) {
    this.state.set('yAxisLabel', payload.yAxisLabel);
  }

  setShowSignal(payload: { showSignal: boolean }) {
    this.state.set('showSignal', payload.showSignal);
  }

  setShowTimeRange(payload: { showTimeRange: boolean }) {
    this.state.set('showTimeRange', payload.showTimeRange);
  }

  setShowCondition(payload: { showCondition: boolean }) {
    this.state.set('showCondition', payload.showCondition);
  }

  setShowBoxplotPoints(payload: { showBoxplotPoints: boolean }) {
    this.state.set('showBoxplotPoints', payload.showBoxplotPoints);
  }

  protected readonly handlers = {
    ...super.baseHandlers,
    SR_SET_ITEMS: this.setItems,
    SR_SET_BASELINE: this.setBaseline,
    SR_ADD_ITEM: this.addItem,
    SR_INSERT_ITEM_AT: this.insertItemAtIndex,
    SR_UPDATE_ITEM: this.updateItem,
    SR_REMOVE_ITEMS: this.removeItems,
    SR_SET_IS_ITEMS_OVERLAID: this.setIsItemsOverlaid,
    SR_SET_STATUS: this.setStatus,
    SR_SET_SHOW_HISTOGRAM: this.setShowHistogram,
    SR_SET_SHOW_BOX_PLOT: this.setShowBoxPlot,
    SR_SET_SHOW_TABLE: this.setShowTable,
    SR_SET_VISIBLE_STATS: this.setVisibleStats,
    SR_SET_SHOW_BELL_CURVE: this.setShowBellCurve,
    SR_SET_X_AXIS_LABEL: this.setXAxisLabel,
    SR_SET_Y_AXIS_LABEL: this.setYAxisLabel,
    SR_SET_SHOW_SIGNAL: this.setShowSignal,
    SR_SET_SHOW_TIME_RANGE: this.setShowTimeRange,
    SR_SET_SHOW_CONDITION: this.setShowCondition,
    SR_SET_SHOW_BOXPLOT_POINTS: this.setShowBoxplotPoints,
  };
}
